import { NEWS_QUANTITY_MAX, NEWS_QUANTITY_MIN, TipoSecaoHome } from '../../../../model/apm/apm-enums';
import { FieldErrors, FieldMap, validateFields } from '../../components/field-config';

export type HomeSectionField = 'title' | 'subtitle' | 'content' | 'itemsQuantity';
export type HomeItemField = 'image' | 'icon' | 'title' | 'description' | 'url' | 'openInNewTab' | 'period';

export interface HomeSectionTypeConfig {
    description: string;
    fields: FieldMap<HomeSectionField>;
    itemNoun?: string;
    itemFields?: FieldMap<HomeItemField>;
}

const title = { label: 'Título', kind: 'text' as const };
const subtitle = { label: 'Subtítulo', kind: 'text' as const };
const link = { label: 'Link', kind: 'url' as const };
const newTab = { label: 'Abrir em nova aba', kind: 'checkbox' as const };

export const HOME_SECTION_CONFIG: Record<TipoSecaoHome, HomeSectionTypeConfig> = {
    [TipoSecaoHome.Banners]: {
        description: 'Banners de corpo da home, exibidos na posição escolhida entre as demais seções. O carrossel do topo é cadastrado na tela de Banners.',
        fields: {},
        itemNoun: 'Banner',
        itemFields: {
            image: { label: 'Imagem', kind: 'image', required: true, requiredMessage: 'Imagem é obrigatória' },
            title: { label: 'Título (texto alternativo)', kind: 'text' },
            url: link,
            openInNewTab: newTab,
            period: { label: 'Período de exibição', kind: 'date' },
        },
    },
    [TipoSecaoHome.DiarioOficial]: {
        description: 'Últimas edições do Diário Oficial. Apenas posição e visibilidade são configuráveis.',
        fields: {},
    },
    [TipoSecaoHome.Noticias]: {
        description: 'Últimas notícias publicadas no portal.',
        fields: {
            itemsQuantity: {
                label: 'Quantidade de notícias', kind: 'number', required: true,
                min: NEWS_QUANTITY_MIN, max: NEWS_QUANTITY_MAX,
                requiredMessage: `A quantidade de notícias deve estar entre ${NEWS_QUANTITY_MIN} e ${NEWS_QUANTITY_MAX}.`,
                rangeMessage: `A quantidade de notícias deve estar entre ${NEWS_QUANTITY_MIN} e ${NEWS_QUANTITY_MAX}.`,
            },
        },
    },
    [TipoSecaoHome.TextoLivre]: {
        description: 'Bloco de texto formatado.',
        fields: { title, subtitle, content: { label: 'Conteúdo', kind: 'richText', required: true } },
    },
    [TipoSecaoHome.AcessoRapido]: {
        description: 'Atalhos com ícone para serviços e páginas.',
        fields: { title, subtitle },
        itemNoun: 'Atalho',
        itemFields: {
            icon: { label: 'Ícone', kind: 'icon', required: true },
            title: { label: 'Título', kind: 'text', required: true },
            url: { ...link, required: true },
            openInNewTab: newTab,
        },
    },
    [TipoSecaoHome.CardDestaque]: {
        description: 'Cards com imagem, título e descrição.',
        fields: { title, subtitle },
        itemNoun: 'Card',
        itemFields: {
            image: { label: 'Imagem', kind: 'image', required: true, requiredMessage: 'Imagem é obrigatória' },
            title: { label: 'Título', kind: 'text', required: true },
            description: { label: 'Descrição', kind: 'multiline' },
            url: link,
            openInNewTab: newTab,
        },
    },
    [TipoSecaoHome.Videos]: {
        description: 'Vídeos do YouTube em destaque.',
        fields: { title, subtitle },
        itemNoun: 'Vídeo',
        itemFields: {
            title: { label: 'Título', kind: 'text' },
            url: { label: 'URL do vídeo (YouTube)', kind: 'youtube', required: true, requiredMessage: 'URL do vídeo é obrigatória' },
        },
    },
};

export const homeSectionHasItems = (type: TipoSecaoHome) => !!HOME_SECTION_CONFIG[type]?.itemFields;

export function validateHomeItem(type: TipoSecaoHome, value: { startDate?: string; endDate?: string; [key: string]: any }): FieldErrors {
    const itemFields = HOME_SECTION_CONFIG[type]?.itemFields || {};
    const errors = validateFields(itemFields, value);
    if (itemFields.period && value.startDate && value.endDate && value.endDate < value.startDate) {
        errors.period = 'A data final de exibição deve ser maior ou igual à data inicial.';
    }
    return errors;
}
